import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePolling } from "@/lib/usePolling";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { DataTable } from "@/components/DataTable";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardList, FilePlus, Pencil, Trash2, Lock, MessageSquarePlus } from "lucide-react";

const fmtEur = (n) => "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) => s ? new Date(s + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
const RapBadge = ({ s }) => s === "trasmesso"
  ? <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700"><Lock className="mr-1 h-3 w-3" />Trasmesso</Badge>
  : <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">Bozza</Badge>;

export default function MieiRapportini() {
  const [items, setItems] = useState([]);
  const [editReq, setEditReq] = useState(null);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const refresh = () => api.get("/rapportini").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);
  usePolling(refresh, 15000);

  const remove = async (id) => { try { await api.delete(`/rapportini/${id}`); toast.success("Eliminato"); refresh(); } catch (e) { toast.error("Errore"); } };

  const openEditReq = (r) => { setEditReq(r); setNote(""); };
  const sendEditReq = async () => {
    if (!editReq) return;
    setSending(true);
    try {
      await api.post(`/rapportini/${editReq.id}/request-edit`, { note });
      toast.success("Richiesta di modifica inviata al responsabile");
      setEditReq(null); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setSending(false); }
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">I miei rapportini</h1>
          <p className="mt-1 text-sm text-slate-500">Compila e trasmetti i tuoi rapportini di cantiere.</p>
        </div>
        <Link to="/nuovo-rapportino"><Button className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]" data-testid="new-rapportino-btn"><FilePlus className="mr-2 h-4 w-4" /> Nuovo rapportino</Button></Link>
      </div>
      <Card className="rounded-lg border-slate-200 shadow-sm">
        <div className="p-4">
          <DataTable data={items} testid="miei-rapportini-table" rowTestId={(r) => `miei-rap-row-${r.id}`}
            searchKeys={["commessa_name", "commessa_code"]} searchPlaceholder="Cerca rapportino..." initialSort={{ key: "date", dir: "desc" }}
            emptyIcon={ClipboardList} emptyText="Nessun rapportino"
            columns={[
              { key: "date", header: "Data", sortable: true, className: "text-slate-500", cell: (r) => fmtDate(r.date) },
              { key: "commessa_name", header: "Commessa", sortable: true, className: "font-medium text-slate-900", cell: (r) => <>{r.commessa_name}{r.commessa_code && <span className="block text-xs text-slate-400">{r.commessa_code}</span>}</> },
              { key: "hours", header: "Ore", sortable: true, className: "text-slate-600", cell: (r) => `${r.hours} h` },
              { key: "revenue", header: "Ricavi", align: "right", sortable: true, className: "text-right text-emerald-700 font-semibold", cell: (r) => fmtEur(r.revenue) },
              { key: "cost_total", header: "Costi", align: "right", sortable: true, className: "text-right text-rose-700", cell: (r) => fmtEur(r.cost_total) },
              { key: "margin", header: "Margine", align: "right", sortable: true, className: "text-right font-heading font-bold text-slate-900", cell: (r) => fmtEur(r.margin) },
              { key: "status", header: "Stato", sortable: true, cell: (r) => <div className="flex items-center gap-1.5"><RapBadge s={r.status} />{r.edit_requested && <Badge variant="outline" className="border-blue-200 bg-blue-50 text-[#002FA7]">Modifica richiesta</Badge>}</div> },
              { key: "actions", header: "Azioni", align: "right", cell: (r) => (
                <div className="flex justify-end gap-2">
                  {r.status === "trasmesso" && !r.edit_requested && <Button size="sm" variant="outline" onClick={() => openEditReq(r)} data-testid={`request-edit-rap-${r.id}`} className="rounded-md border-blue-200 text-[#002FA7] hover:bg-blue-50"><MessageSquarePlus className="mr-1.5 h-4 w-4" />Richiedi modifica</Button>}
                  {r.status === "bozza" && <Link to={`/rapportino/${r.id}`}><Button size="sm" variant="outline" data-testid={`edit-rap-${r.id}`} className="rounded-md"><Pencil className="h-4 w-4" /></Button></Link>}
                  {r.status === "bozza" && <Button size="sm" variant="outline" onClick={() => remove(r.id)} data-testid={`delete-rap-${r.id}`} className="rounded-md border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></Button>}
                </div>
              ) },
            ]} />
        </div>
      </Card>

      <Dialog open={!!editReq} onOpenChange={(o) => !o && setEditReq(null)}>
        <DialogContent data-testid="request-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Richiedi modifica</DialogTitle>
            <DialogDescription>Il rapportino è trasmesso e bloccato. Invia una richiesta al responsabile per sbloccarlo e poterlo modificare.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-500">Motivo / nota (opzionale)</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Es. Ho sbagliato le ore di lavoro..." data-testid="request-edit-note" rows={4} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditReq(null)} className="rounded-md">Annulla</Button>
            <Button onClick={sendEditReq} disabled={sending} data-testid="request-edit-submit" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]"><MessageSquarePlus className="mr-2 h-4 w-4" />Invia richiesta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
